import { h } from "../dom.js";

export type ViewMode = "cards" | "list";

function load(id: string): ViewMode {
  try {
    return localStorage.getItem(`view:${id}`) === "list" ? "list" : "cards";
  } catch {
    return "cards";
  }
}

/**
 * The cards / list switch of one list. Cards are the default; the choice is
 * remembered per list in this browser.
 */
export class ViewToggle {
  mode: ViewMode;
  readonly el: HTMLElement;
  private readonly buttons: Record<ViewMode, HTMLButtonElement>;

  constructor(
    private readonly id: string,
    onChange: () => void,
  ) {
    this.mode = load(id);
    const button = (mode: ViewMode, icon: string, label: string) =>
      h(
        "button",
        {
          type: "button",
          class: "small",
          title: label,
          "aria-label": label,
          onclick: () => {
            if (this.mode === mode) return;
            this.mode = mode;
            try {
              localStorage.setItem(`view:${this.id}`, mode);
            } catch {
              // Private windows may refuse storage; the choice then lasts until the page closes.
            }
            this.sync();
            onChange();
          },
        },
        icon,
      );
    this.buttons = { cards: button("cards", "▦", "Show as cards"), list: button("list", "☰", "Show as a list") };
    this.el = h("div", { class: "segmented view-toggle", role: "group", "aria-label": "View" }, this.buttons.cards, this.buttons.list);
    this.sync();
  }

  get cards(): boolean {
    return this.mode === "cards";
  }

  private sync(): void {
    for (const [mode, b] of Object.entries(this.buttons) as Array<[ViewMode, HTMLButtonElement]>) {
      b.classList.toggle("active", mode === this.mode);
      b.setAttribute("aria-pressed", String(mode === this.mode));
    }
  }
}
